import { useContext, useState } from 'react'
import { Button, InlineAlert } from '../components/BaseComponents'
import { getApiErrorMessage, getCSRFToken, markLogoutNotConfirmed } from '../lib/utils'
import usePublicApi from '../hooks/usePublicApi'
import { performLogoutRequest } from '../lib/logout'
import { useNavigate } from 'react-router-dom'
import routes from '../components/Router/routes'
import { userContext } from '../context/UserContext'
import useConnectionStatus from '../hooks/useConnectionStatus'
import useReconnectProbe from '../hooks/useReconnectProbe'
import ConnectionStatusBanner from '../components/ConnectionStatusBanner/ConnectionStatusBanner'
import ConnectionGuard from '../components/ConnectionGuard/ConnectionGuard'

type VerifyEmailRequiredProps = {
	email?: string
	fullName?: string
}

// Shown by UserContext instead of the app for a leftover session of an unverified account.
function VerifyEmailRequired({ email, fullName }: VerifyEmailRequiredProps) {
	const publicApi = usePublicApi()
	const navigate = useNavigate()
	const { clearUserData, fetchUserData } = useContext(userContext)
	const [busy, setBusy] = useState(false)
	const [sent, setSent] = useState(false)
	const [error, setError] = useState('')
	const isConnected = useConnectionStatus()
	// Issue #857 (item 4): UserContext.tsx short-circuits to this page instead of mounting Router
	// for a signed-in, unverified user, so this is the only probe/banner/guard for that branch -
	// mirrors Router.tsx exactly. Exactly one of Router / OrganisationPickerLayout /
	// VerifyEmailRequired / TrainingNotStarted is ever mounted at a time (UserContext.tsx's
	// short-circuit returns are mutually exclusive), so there is no risk of a duplicate probe.
	const shouldBlockForConnection = !isConnected
	useReconnectProbe(isConnected)

	const onResend = async () => {
		setBusy(true)
		setError('')
		setSent(false)
		try {
			await publicApi.post('/resendVerification', { email, _csrf: await getCSRFToken() })
			setSent(true)
		} catch (err) {
			setError(getApiErrorMessage(err, 'Could not send the verification email. Please try again later.'))
		} finally {
			setBusy(false)
		}
	}

	// For a link opened on another device: re-read the account state.
	const onRecheck = async () => {
		setBusy(true)
		setError('')
		try {
			await fetchUserData()
		} finally {
			setBusy(false)
		}
	}

	const onLogout = async () => {
		try {
			// Issue #759 (final review F1): the CSRF fetch and the POST are both bounded by the
			// same timeout inside performLogoutRequest - see lib/logout.ts.
			await performLogoutRequest(publicApi)
		} catch (err: any) {
			// Issue #759: best-effort - the server-side session will also expire on its own, but
			// the client-side state below is the actual data-leak boundary and must always be
			// cleared, even if this call fails (offline, CSRF mismatch, 5xx, or a request that
			// never settled at all - see withTimeout above).
			console.error('Failed to notify the server of logout, status:', err?.status)
			markLogoutNotConfirmed()
		} finally {
			// Issue #759 (final review F3): clearUserData() already flips userData to undefined,
			// which alone makes Router.tsx redirect to login on the next render (see
			// UserContext.tsx) - a window.location.reload() here was not just redundant, it was
			// actively risky: if the server-side session cookie is still considered valid for any
			// reason (the logout POST above failed), a full reload could silently re-authenticate
			// the previous session instead of landing the user on the login screen.
			clearUserData()
			navigate(routes.publicLogin.path)
		}
	}

	return (
		// Renders outside AppRouter's flex wrapper, so the flex container stops the inner mt-16 collapsing through #root.
		<div className='flex flex-col items-center min-h-dvh'>
			{/* Issue #857 (item 4): wrapped in w-full since the outer wrapper's items-center otherwise
			    shrinks the banner to its content width, unlike Router.tsx's app-shell which stretches
			    children full-width by default. */}
			<div className='w-full'><ConnectionStatusBanner /></div>
			{/* Issue #857 (item 4): ConnectionGuard renders exactly one wrapping div, so passing the
			    pre-existing .content classes here (plus relative, needed for the overlay's absolute
			    inset-0 positioning) preserves the original DOM depth/layout - see
			    TrainingNotStarted.test.tsx's structural assertion for why that matters. Log out is
			    deliberately rendered as a sibling below, outside the guarded/inert region, so it stays
			    reachable even while the rest of this page is hidden. */}
			<ConnectionGuard block={shouldBlockForConnection} className='content w-[32rem] mt-16 text-center relative'>
				<div className='text-charcoal-100 text-2xl'>{fullName ? `Welcome to Neoni, ${fullName}` : 'Welcome to Neoni'}</div>
				<div className='mt-8 text-charcoal-100'>
					Verify your email address to continue. We sent a verification link{email && <> to <span>{email}</span></>}. Check your spam folder if you cannot find it.
				</div>
				{sent && (
					<InlineAlert type='success' className='mt-6'>If your address is not yet verified, a new verification email is on its way.</InlineAlert>
				)}
				{error && <InlineAlert type='error' className='mt-6'>{error}</InlineAlert>}
				<div className='mt-8 flex justify-center gap-4'>
					<Button type='Primary' text='Resend verification email' disabled={busy} onClick={onResend} />
					<Button type='Outlined' text='I have verified my email' disabled={busy} onClick={onRecheck} />
				</div>
				{email && (
					<div className='mt-8 text-charcoal-100'>
						Signed in as <span>{email}</span>.
					</div>
				)}
			</ConnectionGuard>
			<Button type='Outlined' text='Log out' additionalClass={email ? 'mt-6' : 'mt-8'} onClick={onLogout} />
		</div>
	)
}

export default VerifyEmailRequired
